import { Component, OnInit, Input } from '@angular/core';
import { take, catchError } from 'rxjs/operators';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { Warehouse } from '../../models/warehouse.model';
import { WarehouseLocation } from '../../models/warehouse-location.model';
import { WarehouseService } from '../../services/warehouse.service';
import { WarehouseLocationService } from '../../services/warehouse-location.service';
import { StockQuantityService } from '../../services/stock-quantity.service';
import { StockMovementReferenceService } from '../../services/stock-movement-reference.service';
import { ProductStockMovementReference } from '../../models/product-stock-movement-reference.model';
import { WarehouseReportingService } from '../../services/warehouse-reporting.service';
import { StockOperation, StockOperationLine } from '../../models/stock-operation.model';
import { MOVEMENT_ADJUSTMENT, MOVEMENT_IN, MOVEMENT_OUT } from '../../models/product-stock-movement.model';
import { StockWeightService } from '../../services/stock-weight.service';
import { WarehouseLocationCodeService } from '../../services/warehouse-location-code.service';

export const OPERATION_TRANSFER = 'transfer';

@Component({
    selector: 'app-stock-operation-form',
    templateUrl: './stock-operation-form.component.html',
    styleUrls: ['./stock-operation-form.component.scss'],
    standalone: false
})
export class StockOperationFormComponent implements OnInit {

  @Input()
  operation: StockOperation;

  /** in, out, adjustment or transfer. Decides which columns the lines carry. */
  @Input()
  operationType: string = MOVEMENT_IN;

  warehouses: Warehouse[] = [];
  products: Product[] = [];
  references: ProductStockMovementReference[] = [];

  locations: WarehouseLocation[] = [];
  toLocations: WarehouseLocation[] = [];

  /**
   * What the operator typed into the location box, held here rather than on the line because it is
   * transient. A line that has been added carries a resolved id and is drawn through
   * `getLocationName`, so the text it happened to be typed as is of no further interest - and
   * keeping it off `StockOperationLine` means it cannot be posted.
   */
  locationText: string = '';
  toLocationText: string = '';

  /**
   * Batch numbers already seen against each product, keyed by product.
   *
   * A suggestion list and nothing more. There is no batch table, so these are the numbers the ledger
   * has recorded against this product before now — not a set of batches to choose between. An
   * operator receiving a pallet numbered for the first time types a number that is not in here, and
   * that has to keep working: it is how every batch number gets into the system in the first place.
   *
   * Kept per product rather than as one list for the currently selected product, so a line added
   * against product A can still be shown with its batch number after the entry row has moved on to
   * product B. Also means switching back to a product already looked at costs no second request.
   */
  batchNumbersByProduct: any = {};

  /** Products a request is outstanding for, so a re-pick does not queue a duplicate lookup. */
  private loadingBatchesFor: number[] = [];

  // The same aisle and shelf can exist at two sites, so a location is only ever picked
  // through the warehouse that scopes it.
  warehouseId: number = -1;
  toWarehouseId: number = -1;

  newLine: StockOperationLine = new StockOperationLine();

  constructor(private warehouseService: WarehouseService,
    private warehouseLocationService: WarehouseLocationService,
    private productService: ProductService,
    private stockMovementReferenceService: StockMovementReferenceService,
    private warehouseReportingService: WarehouseReportingService,
    private stockQuantityService: StockQuantityService,
    private stockWeightService: StockWeightService,
    private locationCodes: WarehouseLocationCodeService) { }

  ngOnInit(): void {
    this.loadWarehouses();
    this.loadProducts();
    this.loadReferences();
  }

  isTransfer(): boolean {
    return this.operationType === OPERATION_TRANSFER;
  }

  isAdjustment(): boolean {
    return this.operationType === MOVEMENT_ADJUSTMENT;
  }

  loadWarehouses() {
    this.warehouseService.getAllWarehouses()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouses");
      return e;
    }))
    .subscribe((data: Warehouse[]) => {
      this.warehouses = data;
    })
  }

  loadProducts() {
    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load products");
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  loadReferences() {
    this.stockMovementReferenceService.getAllStockMovementReferences()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load movement references");
      return e;
    }))
    .subscribe((data: ProductStockMovementReference[]) => {
      this.references = data;
    })
  }

  /**
   * Changing a warehouse invalidates every location already picked under it, so the lines go
   * with it rather than being posted against the wrong site.
   */
  onWarehouseChanged(warehouseId: number) {
    this.warehouseId = +warehouseId;
    this.locations = [];
    this.newLine.warehouseLocationId = -1;
    // Cleared with the id it resolved to. A code left in the box after the warehouse changed reads
    // as a location that has been accepted, when what it names is a shelf in the site just left.
    this.locationText = '';

    this.clearLines();

    if (this.warehouseId !== -1) {
      this.loadLocationsForWarehouse(this.warehouseId, false);
    }
  }

  onToWarehouseChanged(warehouseId: number) {
    this.toWarehouseId = +warehouseId;
    this.toLocations = [];
    this.newLine.toWarehouseLocationId = -1;
    this.toLocationText = '';

    this.clearLines();

    if (this.toWarehouseId !== -1) {
      this.loadLocationsForWarehouse(this.toWarehouseId, true);
    }
  }

  loadLocationsForWarehouse(warehouseId: number, isDestination: boolean) {
    this.warehouseLocationService.getWarehouseLocationsForWarehouse(warehouseId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load warehouse locations");
      return e;
    }))
    .subscribe((data: WarehouseLocation[]) => {
      if (isDestination) {
        this.toLocations = data;
      } else {
        this.locations = data;
      }
    })
  }

  /**
   * Picking a product clears any batch number already typed, so the batch goes with it.
   *
   * A batch number belongs to a product — it is printed on that product's goods — so a number left
   * behind from the previous pick would be recorded against goods it was never on. Nothing in the
   * backend would refuse it: there is no batch table and so no product-batch pairing to check
   * against, which is exactly why this has to be cleared here. It would be stored, and a traceability
   * report would show one number spanning two products with no defect anywhere to find.
   */
  onLineProductChanged(line: StockOperationLine, productId: any) {
    line.productId = +productId;
    line.batchNumber = null;

    if (line.productId !== -1) {
      this.loadBatchNumbersForProduct(line.productId);
    }
  }

  /**
   * A batch is optional on every operation, so an empty box is a real answer rather than an unfilled
   * field: it is what this input starts on and it is never marked invalid.
   *
   * Mapped to null rather than kept as '', so an un-batched line posts an explicit "no batch". The
   * backend reads '' as "no batch" too, so this is belt and braces on that point — but it also keeps
   * the model honest for `getBatchNumber` below, which would otherwise have to tell '' from null.
   *
   * Deliberately NOT normalised here. `normaliseBatchNumber` on the backend is the single place that
   * decides stored form, and a second normalisation in the browser would be a second opinion about
   * what a batch is — the one thing this design cannot afford. The operator's raw typing is posted and
   * the server decides.
   */
  onLineBatchNumberChanged(line: StockOperationLine, batchNumber: any) {
    const typed = batchNumber === null || batchNumber === undefined ? '' : String(batchNumber).trim();

    line.batchNumber = typed === '' ? null : typed;
  }

  /**
   * A weight is optional on every operation, so an empty box is a real answer rather than an
   * unfilled field: it is what these inputs start on and neither is ever marked invalid.
   *
   * Blank maps to null, so an unweighed line posts an explicit "not weighed". This is the one case
   * that has to be handled here rather than left to the server: `Number('')` is 0, so a cleared box
   * left as '' and coerced anywhere on the way would post a weight of zero — a real reading, on a
   * movement nobody weighed, summing into every total underneath it.
   *
   * Nothing else is done to the value, and that is deliberate. `weightOrNull` on the backend is the
   * single place that decides what a weight is — it refuses a negative, a non-finite and an
   * over-range reading rather than dropping them — and a second opinion in the browser would either
   * silently correct a typo the operator is entitled to be told about, or disagree with the server
   * about the same number. Not rounded here either: the column holds three decimals and the server
   * is what knows that.
   *
   * `end` below `start` is passed straight through. On a goods-out or a transfer the container loses
   * goods, so that is the correct physical reading and a negative net is the right answer.
   */
  onLineStartWeightChanged(line: StockOperationLine, startWeight: any) {
    line.startWeight = this.typedWeight(startWeight);
  }

  onLineEndWeightChanged(line: StockOperationLine, endWeight: any) {
    line.endWeight = this.typedWeight(endWeight);
  }

  /** Blank where the line was not weighed, which is the normal case rather than missing data. */
  getStartWeight(line: StockOperationLine): string {
    return this.stockWeightService.format(line.startWeight);
  }

  getEndWeight(line: StockOperationLine): string {
    return this.stockWeightService.format(line.endWeight);
  }

  /**
   * Net moved, shown as a courtesy so the operator can see what they just typed adds up.
   *
   * Blank unless BOTH readings are present, matching what the backend reports: net is
   * `endWeight - startWeight`, and a line weighed only once has no net rather than a net equal to
   * its one reading. This is not the figure any report totals — that comes back from the server,
   * which is also what excludes the second row of a transfer from a total.
   *
   * Both the subtraction and the blank come from `StockWeightService`, which is the one place that
   * decides what a net is and how a weight is drawn — the reports read the same code, so this cell
   * and the movement history cannot disagree about the same pair of readings.
   */
  getNetWeight(line: StockOperationLine): string {
    return this.stockWeightService.format(
      this.stockWeightService.net(line.startWeight, line.endWeight));
  }

  /**
   * A cleared box is "not weighed", and every other value is the operator's own typing.
   *
   * A `type='number'` input hands back '' both for an empty box and for something the browser
   * cannot read as a number, so an unusable entry lands here as blank and is posted as not weighed
   * rather than as a wrong number. Anything the browser did read is passed on untouched for the
   * server to accept or refuse.
   */
  private typedWeight(weight: any): number {
    const typed: string = weight === null || weight === undefined ? '' : String(weight).trim();

    return typed === '' ? null : Number(typed);
  }

  /**
   * Batch numbers already recorded against the product, as suggestions — NOT a list to choose from,
   * and NOT the batches this location is holding.
   *
   * Two things this is deliberately not. It is not a closed list: a pallet numbered for the first
   * time has a number that has never been seen, so the field it backs is free text and this only
   * saves typing. And it is not filtered to what the shelf holds, because batch balances are not
   * enforced and are not consulted at posting time — a picker filtered that way would claim a control
   * that does not exist, and would hide a batch whose balance has gone negative through a mis-scan,
   * which is exactly the case somebody needs to be able to book against while they correct it.
   */
  getBatchNumbersForProduct(productId: number): string[] {
    return this.batchNumbersByProduct[productId] ? this.batchNumbersByProduct[productId] : [];
  }

  /** Blank where the line carries no batch, which is the normal case rather than missing data. */
  getBatchNumber(line: StockOperationLine): string {
    return line.batchNumber ? line.batchNumber : '';
  }

  /**
   * Loaded on demand per product rather than up front for all of them: batch numbers accumulate one
   * per delivery, so the whole set is unbounded where the reference and warehouse lookups are not.
   *
   * `productId` is always sent. Without it the endpoint answers with every batch number across every
   * product, which for a per-product suggestion list is not a wider answer but a wrong one.
   *
   * A failure here is deliberately silent. The field is optional and free text, so an operator whose
   * suggestion lookup failed can still type the number and post exactly as they could before this
   * field existed — an alert would turn an unavailable convenience into an interruption on the main
   * path. This is why the list is only ever suggestions: there is no state in which its absence stops
   * a batch being recorded.
   */
  loadBatchNumbersForProduct(productId: number) {
    if (this.batchNumbersByProduct[productId] || this.loadingBatchesFor.indexOf(productId) !== -1) {
      return;
    }

    this.loadingBatchesFor.push(productId);

    this.warehouseReportingService.getBatchNumbers(productId)
    .pipe(take(1))
    .pipe(catchError((e) => {
      this.loadingBatchesFor = this.loadingBatchesFor.filter(id => id !== productId);
      return e;
    }))
    .subscribe((data: string[]) => {
      this.batchNumbersByProduct[productId] = data;
      this.loadingBatchesFor = this.loadingBatchesFor.filter(id => id !== productId);
    })
  }

  /**
   * qty is a float, so it is rounded here on the way in rather than at comparison time.
   * Rounding on blur rather than on each keystroke leaves a part typed decimal alone.
   */
  onQtyChanged(line: StockOperationLine) {
    line.qty = this.stockQuantityService.round(line.qty);
  }

  isQtyInvalid(line: StockOperationLine): boolean {
    if (this.isAdjustment()) {
      return this.stockQuantityService.isZero(line.qty);
    }

    return !this.stockQuantityService.isPositive(line.qty);
  }

  addLine() {
    this.newLine.qty = this.stockQuantityService.round(this.newLine.qty);

    if (this.newLine.productId === -1 || this.newLine.warehouseLocationId === -1) {
      alert('Please choose a product and enter a location code that matches a real location');
      return;
    }

    if (this.isQtyInvalid(this.newLine)) {
      alert(this.isAdjustment()
        ? 'An adjustment quantity must not be zero'
        : 'Please enter a quantity greater than zero');
      return;
    }

    if (this.isTransfer()) {
      if (this.newLine.toWarehouseLocationId === -1) {
        alert('Please enter a destination location code that matches a real location');
        return;
      }

      if (this.newLine.toWarehouseLocationId === this.newLine.warehouseLocationId) {
        alert('A transfer must move stock between two different locations');
        return;
      }
    }

    this.newLine.direction = this.getDirectionForLine(this.newLine);

    this.operation.lines.push(JSON.parse(JSON.stringify(this.newLine)));

    this.newLine = new StockOperationLine();
    // Reset with the line, for the reason the batch box's own comment gives: the model going back to
    // its defaults is not enough on its own, because the box is `[value]` bound and would otherwise
    // still be showing the code just used while the new line carries no location at all.
    this.locationText = '';
    this.toLocationText = '';
  }

  deleteLine(index: number) {
    this.operation.lines.splice(index, 1);
  }

  getProductName(productId: number): string {
    const product = this.products.filter(p => p.id === productId)[0];

    return product ? product.name : '';
  }

  getLocationName(locationId: number, isDestination: boolean = false): string {
    const source = isDestination ? this.toLocations : this.locations;

    const location = source.filter(l => l.id === locationId)[0];

    return location ? location.aisle + ' / ' + location.shelf : '';
  }

  /**
   * The operator types a location code and it is resolved against the locations actually loaded for
   * the selected warehouse. Nothing is ever guessed: the box holds text, `warehouseLocationId` holds
   * a real id or -1, and only the id is posted, so an unresolved code cannot reach the ledger.
   *
   * This replaced a dropdown. A picker cannot be typed at, and an operator putting a pallet away
   * already knows the code on the shelf label - reading it off a list of every location in the
   * building is slower than typing it, and gets slower as the building fills up.
   */
  onLocationTextChanged(text: string, isDestination: boolean = false) {
    if (isDestination) {
      this.toLocationText = text;
      this.newLine.toWarehouseLocationId = this.locationCodes.resolve(text, this.toLocations);

      return;
    }

    this.locationText = text;
    this.newLine.warehouseLocationId = this.locationCodes.resolve(text, this.locations);
  }

  /**
   * The confirmation drawn beside the box: what the typed code actually resolved to, in the same
   * `aisle / shelf` form the added lines and the reports use. Blank until it resolves.
   *
   * Worth drawing rather than assuming the operator can see it. The box shows what they typed; this
   * shows what the system read it as, and on a code like `A11` against aisles A and A1 those are
   * the two things a mis-put-away turns on.
   */
  resolvedLocationLabel(isDestination: boolean = false): string {
    const id = isDestination
      ? this.newLine.toWarehouseLocationId
      : this.newLine.warehouseLocationId;

    return id === -1 ? '' : this.getLocationName(id, isDestination);
  }

  /**
   * Why a typed code did not resolve, or blank when it did or when nothing has been typed yet.
   *
   * An empty box says nothing, because an operator part-way through a line has not made a mistake -
   * `addLine` is what refuses an unset location, and it already did before this box existed. This is
   * the opposite call to the weight boxes further down the form, which are never marked invalid at
   * all: a location is required and a weight is not.
   */
  locationError(isDestination: boolean = false): string {
    return this.locationCodes.error(
      isDestination ? this.toLocationText : this.locationText,
      isDestination ? this.toLocations : this.locations);
  }

  /**
   * in and out carry a positive qty with the direction implied by the operation; an adjustment
   * carries a signed qty, so its direction follows the sign.
   */
  private getDirectionForLine(line: StockOperationLine): string {
    if (this.isAdjustment()) {
      return this.stockQuantityService.isNegative(line.qty) ? MOVEMENT_OUT : MOVEMENT_IN;
    }

    if (this.isTransfer()) {
      return MOVEMENT_OUT;
    }

    return this.operationType;
  }

  private clearLines() {
    if (this.operation.lines.length === 0) {
      return;
    }

    this.operation.lines = [];

    alert('The lines have been cleared because their locations belonged to the warehouse you changed');
  }
}
